import { useAuth } from "../contexts/integrated-auth";
import { Loader2, LogIn } from "lucide-react";
import { Redirect, Route, Link } from "wouter";
import { useEffect } from "react";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";

interface ProtectedRouteProps {
  path: string;
  component: React.ComponentType;
  /** If true, show sign-in prompt instead of redirecting to home */
  showSignInPrompt?: boolean;
  /** Custom title for the sign-in prompt */
  promptTitle?: string;
  /** Custom description for the sign-in prompt */
  promptDescription?: string;
}

// Sign-in prompt component shown when user needs to authenticate
function SignInPrompt({
  title = "Sign in to continue",
  description = "You need to be signed in to access this page."
}: {
  title?: string;
  description?: string;
}) {
  return (
    <Layout pageTitle="Sign In Required">
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="w-full max-w-md mx-4">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center">
              <LogIn className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">{title}</CardTitle>
            <CardDescription className="text-base mt-2">
              {description}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-6 text-lg font-semibold"
              asChild
            >
              <Link href="/login">
                <LogIn className="w-5 h-5 mr-2" />
                Sign In
              </Link>
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              Don't have an account?{" "}
              <Link href="/login" className="text-purple-600 hover:text-purple-700 font-medium">
                Create one for free
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

export function ProtectedRoute({
  path,
  component: Component,
  showSignInPrompt = true,
  promptTitle,
  promptDescription,
}: ProtectedRouteProps) {
  const { user, isLoading, isAuthenticated, refetch } = useAuth();

  // Enhanced debugging for auth issues
  console.log(`[DEBUG] Protected route check for '${path}':`, {
    isAuthenticated,
    isLoading,
    hasUser: !!user,
    userId: user?.id,
    time: new Date().toISOString()
  });

  // Refetch auth status if needed - using Replit authentication only
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      console.log("[DEBUG] Refetching auth status for protected route");
      refetch();
    }
  }, [isLoading, isAuthenticated, refetch]);

  // Show loading state while authentication is in progress
  if (isLoading) {
    return (
      <Route path={path}>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-border" />
        </div>
      </Route>
    );
  }

  // Show sign-in prompt or redirect if not authenticated
  if (!isAuthenticated) {
    try {
      // Save the current path to localStorage so we can return after login
      localStorage.setItem('authRedirectPath', window.location.pathname);
      console.log('[DEBUG] Saved redirect path:', window.location.pathname);
    } catch (e) {
      console.error('[DEBUG] Error saving redirect path:', e);
    }

    // Show sign-in prompt instead of silent redirect
    if (showSignInPrompt) {
      return (
        <Route path={path}>
          <SignInPrompt title={promptTitle} description={promptDescription} />
        </Route>
      );
    }

    // Fallback to redirect if showSignInPrompt is false
    return (
      <Route path={path}>
        <Redirect to="/" />
      </Route>
    );
  }

  // Check if user needs to complete onboarding
  const needsOnboarding = user?.username?.startsWith('user_');
  const isOnboardingPage = path === '/onboarding';
  const isInvitePage = path.startsWith('/invite/');

  // If user needs onboarding and is not on onboarding/invite page, redirect
  if (needsOnboarding && !isOnboardingPage && !isInvitePage) {
    console.log('[DEBUG] User needs onboarding, redirecting to /onboarding');
    return (
      <Route path={path}>
        <Redirect to="/onboarding" />
      </Route>
    );
  }

  // User is authenticated and has completed onboarding (or is on onboarding page), render the protected component
  return (
    <Route path={path}>
      <Component />
    </Route>
  );
}